import Head from 'next/head'
import { Box } from 'theme-ui'

import {
  CmsParagraphAdFormatsLayout,
  CmsParagraphAdFormatsLayoutAsset,
  CmsParagraphAdFormatsTechnicalSpecs,
  CmsParagraphAdSpecPageDetailedInfo,
  CmsParagraphAdSpecPageHeader,
  CmsParagraphImage,
  CmsParagraphText
} from 'src/types/adsales/models/cms'
import { JsonApiCMSAdFormatsPage } from 'src/types/adsales/models/cmsPages'
import { JsonApiResource, JsonApiResourceIdentifier } from 'src/types/json-api'
import Default from 'src/layouts/Default'
import PageHeader from 'src/components/pageHeader/PageHeader'
import Background from 'src/components/background/Background'
import { BackgroundColorTypes } from 'src/theme/adSales/colors'
import DetailsSection, { Detail } from 'src/components/pages/adFormats/details/Details'
import { adFormatsHeaderFields, adFormatsPageDetails } from 'src/lib/react-query/fields'
import { findIncluded, includedRelation, includedRelations } from 'src/lib/jsonApi'
import LayoutSection from 'src/components/pages/adFormats/layout/Layout'
import TextBlock from 'src/components/textAndImageBlock/TextBlock'
import TechSpecsSection from 'src/components/pages/adFormats/TechnicalSpecs/TechnicalSpecs'

const getIncludedValue = (res: JsonApiCMSAdFormatsPage, d?: JsonApiResourceIdentifier) =>
  (findIncluded(res.included, d) as JsonApiResource<any>)['attributes']['name']

const getSubtitleValue = (
  res: JsonApiCMSAdFormatsPage,
  label: string,
  pageHeader: JsonApiResource
) =>
  pageHeader['relationships'][label]['data'].map((d: JsonApiResourceIdentifier<any> | undefined) =>
    getIncludedValue(res, d)
  )

export const getAdFormatPageData = (res: JsonApiCMSAdFormatsPage) => {
  const { platformCategory, digitalAdFormatSuite } = adFormatsHeaderFields
  const header = includedRelation(res, 'field_ad_spec_page_header') as JsonApiResource<
    CmsParagraphAdSpecPageHeader['attributes']
  >
  const imageData = header['relationships'][adFormatsHeaderFields.image]['data']

  const image = {
    src: imageData
      ? (findIncluded(res.included, imageData) as JsonApiResource<CmsParagraphImage>).attributes.uri
          ?.url ?? ''
      : null,
    alt: imageData?.meta?.alt ?? ''
  }

  const optionalDescriptionInfo = includedRelation(
    res,
    'field_ad_spec_page_optional'
  ) as JsonApiResource<CmsParagraphText['attributes']>

  const optionalDescription = optionalDescriptionInfo
    ? {
        title: optionalDescriptionInfo.attributes.field_text_title || undefined,
        text: optionalDescriptionInfo.attributes.field_text_description
          ? optionalDescriptionInfo.attributes.field_text_description.value
          : undefined,
        backgroundColor: (optionalDescriptionInfo.attributes.field_text_background_colour ===
        'secondary'
          ? 'background.tertiary'
          : 'background.' +
            optionalDescriptionInfo.attributes.field_text_background_colour) as BackgroundColorTypes
      }
    : null

  const layoutsInfo = includedRelations<JsonApiResource<CmsParagraphAdFormatsLayout>>(
    res,
    'field_ad_formats_layout'
  )

  const layouts = layoutsInfo.map(layout => {
    const imageData = layout['relationships']['field_ad_format_layout_image']['data']
    const assetsData = layout['relationships']['field_ad_format_layout_asset_lis']['data']

    return {
      title: layout['attributes']['field_ad_format_layout_title'],
      description: layout['attributes']['field_ad_format_layout_descripti'] || '',
      image: {
        src: imageData
          ? (findIncluded(res.included, imageData) as JsonApiResource<CmsParagraphImage>).attributes
              .uri?.url ?? ''
          : '',
        alt: imageData?.meta?.alt ?? ''
      },
      assets: assetsData.map((d: JsonApiResourceIdentifier<any> | undefined) => {
        const data = findIncluded<JsonApiResource<CmsParagraphAdFormatsLayoutAsset>>(
          res.included,
          d
        )

        return (
          data && {
            label: data['attributes']['field_ad_format_asset_name'],
            value: data['attributes']['field_ad_format_layout_spec_info']
          }
        )
      })
    }
  })

  const detailInfo = includedRelation(res, 'field_ad_spec_page_detailed_info') as JsonApiResource<
    CmsParagraphAdSpecPageDetailedInfo['attributes']
  >

  const technicalSpecBackground =
    res['data']['attributes']['field_ad_formats_technical_backg'] ?? 'quaternary'
  const technicalSpecInfo = includedRelations<JsonApiResource<CmsParagraphAdFormatsTechnicalSpecs>>(
    res,
    'field_ad_formats_technical_speci'
  )
  const technicalSpecs = technicalSpecInfo.map(spec => ({
    label: spec['attributes']['field_ad_formats_tech_spec_title'],
    description: spec['attributes']['field_ad_formats_tech_spec_text']
      ? spec['attributes']['field_ad_formats_tech_spec_text']['value']
      : null,
    text: spec['attributes']['field_ad_formats_tech_spec_descr'],
    link: spec['attributes']['field_ad_formats_tech_spec_hyper']
  }))
  const platformCategories = getSubtitleValue(res, platformCategory, header)
  const digitalAdSuites = getSubtitleValue(res, digitalAdFormatSuite, header)

  return {
    header,
    image,
    optionalDescription,
    detailInfo,
    platformCategories,
    digitalAdSuites,
    layouts,
    technicalSpecBackground,
    technicalSpecs
  }
}

const AdFormatsPage = ({ res }: { res: JsonApiCMSAdFormatsPage }) => {
  const {
    header: pageHeader,
    image,
    optionalDescription,
    detailInfo,
    platformCategories,
    digitalAdSuites,
    layouts,
    technicalSpecBackground,
    technicalSpecs
  } = getAdFormatPageData(res)
  const { backgroundColor } = adFormatsHeaderFields
  const pageHeaderBackgroundColour =
    `background.${pageHeader['attributes'][backgroundColor]}` as BackgroundColorTypes
  const pageHeaderTitle = pageHeader['attributes']['field_ad_spec_page_header_title']
  const pageHeaderDescription = pageHeader?.attributes?.field_ad_spec_page_header_descri
    ? pageHeader?.attributes?.field_ad_spec_page_header_descri.value
    : ''
  const pageAlias = res.data.attributes.path?.alias?.split('/').pop() ?? ''
  const subtitles: string[] = []
  for (let i = 0; i < [...platformCategories, ...digitalAdSuites].length; i++) {
    if (i < platformCategories.length) subtitles.push(platformCategories[i])
    if (i < digitalAdSuites.length) subtitles.push(digitalAdSuites[i])
  }

  const pageDetails: Detail[] = []
  Object.entries(adFormatsPageDetails).map(entry => {
    const { label, path } = entry[1]
    const key = detailInfo['relationships'][path]

    if (key['data']) {
      let data = null
      if (Array.isArray(key['data'])) {
        data = key['data'].length > 0 ? key['data'] : null
      } else {
        data = [key['data']]
      }

      if (data) {
        pageDetails.push({ label, value: data.map(d => getIncludedValue(res, d)).join(', ') })
      }
    }
  })

  const detailInfoBackground =
    detailInfo['attributes']['field_ad_spec_page_detail_backgr'] ?? 'quaternary'
  const detailInfoText = findIncluded(res.included, detailInfo)
  detailInfoText
    ? pageDetails.push(
        {
          label: 'Ad Format',
          value: detailInfoText['attributes']['field_ad_spec_page_detail_format']
        },
        {
          label: 'Ad Placement',
          value: detailInfoText['attributes']['field_ad_spec_page_detail_place']
        }
      )
    : null

  return (
    <Default>
      <Head>
        <title>
          {pageHeader?.attributes?.field_ad_spec_page_header_title
            ? `${pageHeader.attributes.field_ad_spec_page_header_title} - Ad Formats - BBC Studios Advertising`
            : 'BBC Studios Advertising'}
        </title>
        <meta
          name="description"
          content="BBC Studios creates and distributes globally acclaimed content that shapes culture and drives conversation. For advertisers, it's a powerful opportunity to connect with audiences in the moments that matter — where influence is built, loyalty is earned, and brands make a lasting impact."
        />
        <meta
          name="keywords"
          content="BBC Studios, Advertising, Media, Content Distribution, Brand Partnerships"
        />
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      </Head>
      <Box as="main">
        <Background backgroundColor={pageHeaderBackgroundColour}>
          <PageHeader
            type={'Ad Formats'}
            title={pageHeaderTitle}
            description={pageHeaderDescription}
            image={image || undefined}
            breadcrumbs={[{ name: 'Ad Formats', link: '/ad-formats' }, { name: pageAlias }]}
            backgroundColor={pageHeaderBackgroundColour}
            subTitle={subtitles}
          />
        </Background>
        {optionalDescription ? (
          <Background backgroundColor={optionalDescription['backgroundColor']}>
            <TextBlock
              description={optionalDescription['text'] + ''}
              title={optionalDescription['title']}
              options={{ charLimit: 2000 }}
            />
          </Background>
        ) : null}
        <Background backgroundColor={'background.quaternary'}>
          {layouts.map(layout => (
            <LayoutSection key={layout.title} props={{ ...layout }} />
          ))}
        </Background>
        {technicalSpecs ? (
          <Background
            backgroundColor={`background.${technicalSpecBackground}` as BackgroundColorTypes}
          >
            <TechSpecsSection specs={technicalSpecs} />
          </Background>
        ) : null}
        <Background backgroundColor={`background.${detailInfoBackground}` as BackgroundColorTypes}>
          <DetailsSection details={pageDetails} />
        </Background>
      </Box>
    </Default>
  )
}

export default AdFormatsPage
